import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { AbilityService } from 'ability';
import { isNil } from 'lodash';
import { PagedResponse } from 'mbs-ui-shared@models';
import { PermissionsEnum } from 'mbs-ui-shared@models/PermissionsEnum';
import { DomainInfo, DomainPayload, DomainsQueryParams, UpdateDomainPayload } from 'mbs-ui-shared@models/domain.model';
import { DomainsService } from 'mbs-ui-shared@services/domains.service';
import { Observable, distinctUntilChanged, filter, map, tap, withLatestFrom } from 'rxjs';
import { DomainsStore } from '../store/domains/domainsStore';
import { CustomDomainsStoreProperties } from '../store/domains/domainsStore.types';
import { PagingStoreFacade } from '../store/pagingStoreFactory/pagingStore.facade';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DomainsFacade extends PagingStoreFacade<DomainInfo, CustomDomainsStoreProperties> {
  constructor(
    public store: Store,
    public domainsStore: DomainsStore,
    private domainsService: DomainsService,
    private ability: AbilityService
  ) {
    super(store, domainsStore);
    this.initStreams();
  }

  // specific
  getRestrictedNames$ = this.store.select(this.domainsStore.selectors.selectRestrictedNames);
  getRestrictedNamesLoaded$ = this.store.select(this.domainsStore.selectors.selectLoadedRestrictedNames);
  getRestrictedNamesWhenLoaded$ = this.getRestrictedNames$.pipe(
    withLatestFrom(this.getRestrictedNamesLoaded$),
    filter(([, loaded]) => loaded),
    map(([data]) => data)
  );

  getDomainStatistic$ = this.store.select(this.domainsStore.selectors.selectDomainStatistic);
  getDomainStatisticLoaded$ = this.store.select(this.domainsStore.selectors.selectLoadedDomainStatistic);
  getDomainStatisticLoading$ = this.store.select(this.domainsStore.selectors.selectLoadingDomainStatistic);

  initStreams(): void {
    this.getPagingLoaded$.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe((loaded) => {
      loaded ? this.loadDomainStatistic() : this.resetDomainStatistic();
    });
  }

  // service
  getDomains(params?: DomainsQueryParams): Observable<PagedResponse<DomainInfo>> {
    return this.domainsService.get(params);
  }

  getByName(name: string): Observable<DomainInfo> {
    return this.domainsService.getByName(name);
  }

  create(payload: DomainPayload[]): Observable<DomainInfo> {
    return this.domainsService.create(payload).pipe(tap(() => this.invalidateEntities()));
  }

  update(payload: UpdateDomainPayload): Observable<DomainInfo> {
    return this.domainsService.update(payload).pipe(tap((domain) => this.invalidateEntities(domain)));
  }

  delete(ids: string[]): Observable<null> {
    return this.domainsService.deleteDomains(ids).pipe(tap(() => this.invalidateEntities()));
  }

  getAvailableDomainNames(payload: string[]): Observable<string[]> {
    return this.domainsService.getAvailableDomainNames(payload);
  }

  loadDomainStatistic(force = false): void {
    this.store.dispatch(this.domainsStore.actions.loadDomainStatistic({ force }));
  }

  private resetDomainStatistic(): void {
    this.store.dispatch(this.domainsStore.actions.setDomainStatistic({ domainStatistic: null }));
  }

  loadRestrictedNames(force = false): void {
    this.store.dispatch(this.domainsStore.actions.loadRestrictedNames({ force }));
  }

  exportTo(type: 'html' | 'csv'): void {
    this.domainsService.exportTo(type);
  }

  checkSpecificAccessAppsUsers(companyId: string): boolean {
    return (
      this.ability.can('read', 'Provider') ||
      (this.ability.can('read', 'SubAdmin') &&
        this.ability.can('read', PermissionsEnum.GoogleApps) &&
        (this.ability.can('read', PermissionsEnum.AccessToAllCompanies) ||
          (!this.ability.can('read', PermissionsEnum.AccessToAllCompanies) && !isNil(companyId))))
    );
  }

  private invalidateEntities(domain?: DomainInfo): void {
    this.refreshAfterCRUD(domain, 'domainName');
  }
}
